import { auth } from '../firebase';

const EXECUTE_API_URL = (process.env.REACT_APP_EXECUTE_API_URL || 'http://localhost:8080').replace(
  /\/$/,
  ''
);

const CLIENT_DEADLINE_MS = 90_000;
const POLL_WAIT_MS = 25_000;

function abortError(message = 'Aborted') {
  const error = new Error(message);
  error.name = 'AbortError';
  return error;
}

async function responseError(response) {
  try {
    const body = await response.json();
    return new Error(body?.error || `Server error: ${response.status}`);
  } catch {
    return new Error(`Server error: ${response.status}`);
  }
}

/**
 * Submit Python to the EC2 execute API, then long-poll until the worker
 * writes a terminal result (or the client deadline is reached).
 * @param {string} code
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<string>}
 */
export async function executePythonCode(code, { signal } = {}) {
  if (signal?.aborted) {
    throw abortError();
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('Sign in to run code.');
  }
  const token = await user.getIdToken();

  const submit = await fetch(`${EXECUTE_API_URL}/execute`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ code }),
    signal,
  });

  if (!submit.ok) {
    throw await responseError(submit);
  }

  const submitted = await submit.json();
  if (!submitted?.jobId) {
    throw new Error('Server did not return a job id');
  }

  const deadline = Date.now() + CLIENT_DEADLINE_MS;

  while (Date.now() < deadline) {
    if (signal?.aborted) {
      throw abortError();
    }

    const waitMs = Math.min(POLL_WAIT_MS, Math.max(0, deadline - Date.now()));
    const response = await fetch(
      `${EXECUTE_API_URL}/results/${submitted.jobId}?waitMs=${waitMs}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      }
    );

    if (response.status === 404) {
      throw new Error('Execution job was not found');
    }
    if (response.status === 202) {
      continue;
    }
    if (!response.ok) {
      throw await responseError(response);
    }

    const body = await response.json();
    if (typeof body.output === 'string') {
      return body.output;
    }
    throw new Error('Invalid result payload');
  }

  throw abortError('Execution timed out');
}
